import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

/**
 * App-like splash that only appears on mobile viewports.
 * Two entry points: `Get Scouted` (opens the Hinge-style profile flow) or
 * `Explore the site` (dismisses and scrolls into the marketing home).
 * Dismissal persists for the session.
 */
export default function MobileSplash({ onGetScouted }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const dismissed = sessionStorage.getItem("vv_splash_dismissed") === "1";
    if (isMobile && !dismissed) {
      setVisible(true);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("vv_splash_dismissed", "1");
    document.body.style.overflow = "";
    setVisible(false);
  };

  const openScouted = () => {
    dismiss();
    onGetScouted?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="mobile-splash"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, filter: "blur(6px)" }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[95] md:hidden bg-[#0a0a0a] flex flex-col"
        >
          {/* Backdrop shimmer */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_60%)]" />

          {/* Wordmark */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="relative pt-14 px-8"
          >
            <p className="font-display text-2xl font-black tracking-tighter uppercase text-white">
              Vogue<span className="text-white/70">Verve</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/40 mt-1">
              Model Management · Est. MMXXV
            </p>
          </motion.div>

          {/* Hero copy */}
          <div className="relative flex-1 flex flex-col justify-center px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-script text-white text-5xl leading-none mb-4">
                where were you
              </p>
              <h1 className="font-display font-black uppercase text-white tracking-tighter leading-[0.85] text-[18vw]">
                Darling?
              </h1>
              <p className="text-sm text-white/60 leading-relaxed mt-6 max-w-xs">
                Two ways in. Build your profile like an app, or take the scenic tour first.
              </p>
            </motion.div>
          </div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="relative px-6 pb-10 space-y-3"
          >
            <button
              data-testid="splash-get-scouted-btn"
              onClick={openScouted}
              className="w-full flex items-center justify-between rounded-full bg-white text-black px-7 py-5 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
            >
              Get Scouted
              <ArrowRight size={18} strokeWidth={2.4} />
            </button>
            <button
              data-testid="splash-explore-btn"
              onClick={dismiss}
              className="w-full flex items-center justify-between rounded-full bg-transparent border border-white/25 text-white px-7 py-5 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
            >
              Explore the site
              <ChevronDown size={18} strokeWidth={2.4} />
            </button>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/30 text-center pt-3">
              Swipe. Build. Get seen.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
